import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import { Plus, X, Save, ArrowLeft, Printer, Calculator, History, Lock, TrendingUp, Layers, FilePlus } from "lucide-react";
import { Select, DatePicker } from "antd";
import useApiMethods from "../../lib/useApiMethods";
import ToastMsg from "../../components/common/ToastMsg";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { Input, Textarea } from "../../components/ui/input";
import { AuthContext } from "../../context/AuthContext";
import {
  QUOTATIONS_URL, QUOTATION_DETAIL_URL,
  QUOTATION_MODULES_URL, CUSTOMERSLIST,
  PRICING_RATES_URL, PLAN_SERVICES_URL,
  QUOTATION_APPROVE_URL, QUOTATION_REJECT_URL,
  QUOTATION_VERSIONS_URL, QUOTATION_VERSION_URL,
  QUOTATION_FINALIZE_URL,
  QUOTATION_CUSTOMER_PROJECTS_URL, QUOTATION_TEMPLATE_URL,
} from "../../constants/api";

const PRODUCT_OPTS = [
  { value: "cms",  label: "Clinical Management System" },
  { value: "hims", label: "Hospital Information Management System" },
];
const TIER_OPTS = [
  { value: "essential",  label: "Essential" },
  { value: "advanced",   label: "Advanced" },
  { value: "enterprise", label: "Enterprise" },
];
// Deployment now decides which rate sheet the price is read from, so its values
// match ProductPricingRate.DEPLOYMENT on the server instead of describing the
// hosting in prose.
const DEPLOY_OPTS = [
  { value: "cloud",  label: "On Cloud" },
  { value: "onprem", label: "On Premise" },
];

// Which tiers each deployment sells. Mirrors PLANS_BY_DEPLOYMENT on the model —
// the server is what actually refuses an out-of-ladder price; this only stops
// the picker offering one.
const TIERS_BY_DEPLOYMENT = {
  cloud:  ["essential", "advanced", "enterprise"],
  onprem: ["enterprise"],
};

// Quotations written before the two rate sheets existed stored deployment as
// loose description: 'server' | 'local'. The same two things under older names,
// so an old quote reopens against the sheet it was always priced from.
const LEGACY_DEPLOYMENT = { server: "cloud", local: "onprem" };
const normDeployment = (v) => LEGACY_DEPLOYMENT[v] || (v === "onprem" ? "onprem" : "cloud");

const blankPlan = (idx) => ({
  plan_label: ["Plan A", "Plan B", "Plan C"][idx] || `Plan ${idx + 1}`,
  package_id: null,
  package_title: "",
  max_beds: 0,
  transaction_count_per_year: 0,
  base_price: 0,
  discount_amount: 0,
  gst_rate: 18,
  amc_rate: 40,
  is_recommended: false,
  sort_order: idx,
  items: [],
  pricing_product: "",
  pricing_plan_tier: "",
  pricing_op_per_year: 0,
  pricing_deployment: "cloud",
  // Per-unit pricing (TrustEdge per user, HRZyra per employee, eMedAyush per
  // bed). Seeded from the project's template when the quote is in that project.
  unit_label: "",
  unit_count: 0,
  unit_price: 0,
  billing_months: 12,
});

// Per unit when a unit label is set; otherwise the CMS/HIMS tier calculator.
const isPerUnit = (plan) => !!(plan.unit_label || "").trim();
const unitBase  = (plan) => Number(plan.unit_count || 0) * Number(plan.unit_price || 0)
                            * (Number(plan.billing_months) === 12 ? 12 : 1);
// Custom lines (kind 'line') are charged on top of the package price, unlike
// add-ons, which are quoted aside.
const lineAmount  = (it) => Number(it.quantity || 0) * Number(it.price || 0);
const customTotal = (plan) => (plan.items || []).filter(it => it.kind === "line")
                                .reduce((s, it) => s + lineAmount(it), 0);
const packageBase = (plan) => isPerUnit(plan) ? unitBase(plan) : Number(plan.base_price || 0);
// What the discount comes off: package + custom lines.
const subTotal = (plan) => packageBase(plan) + customTotal(plan);

const fmt = (v) => v ? `₹${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 })}` : "₹0";

// /quotations/:id and /quotations/new render this same component, so React
// would keep one instance across them — and "New" for another project would
// leave the previous quotation's customer, project and plans in the form,
// ready to be saved as a copy. Keying on the URL gives every quotation, and
// every "new for <project>", a fresh form.
export default function QuotationComposerRoute() {
  const loc = useLocation();
  return <QuotationComposer key={loc.pathname + loc.search} />;
}

function QuotationComposer() {
  const { id } = useParams();
  const isEdit = !!id;
  const [searchParams] = useSearchParams();
  const nav = useNavigate();
  const { fetch, create, update } = useApiMethods();
  const { hasPermission, loginUser } = useContext(AuthContext);
  const isSuperAdmin = loginUser?.login_role === "super_admin";
  const canCreate    = isSuperAdmin || hasPermission("quotations", "create");
  const canEdit      = isSuperAdmin || hasPermission("quotations", "edit");
  // Save button is gated on either create (new quote) or edit (existing).
  const canSave      = isEdit ? canEdit : canCreate;

  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    // "Create for <project>" on another quotation opens this page pre-filled.
    customer_id: Number(searchParams.get("customer_id")) || null,
    project_id: Number(searchParams.get("project_id")) || null,
    quote_date: dayjs().format("YYYY-MM-DD"),
    valid_until: dayjs().add(15, "day").format("YYYY-MM-DD"),
    notes: "",
    plans: [blankPlan(0)],
  });
  const [quoteInfo, setQuoteInfo] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [modules, setModules]     = useState([]);
  const [planServices, setPlanServices] = useState([]); // from /plan-services/
  // One rate sheet per deployment × product. On-premise is fetched alongside
  // cloud and simply comes back empty for a tenant that does not sell it.
  const [pricingRates, setPricingRates] = useState({
    cloud:  { cms: [], hims: [] },
    onprem: { cms: [], hims: [] },
  });
  const [onPremAvailable, setOnPremAvailable] = useState(false);
  // The lead's projects, each with its quotations: the "other projects" panel
  // and the Project picker. Empty for a lead in no project.
  const [leadProjects, setLeadProjects] = useState([]);
  const [mergeIds, setMergeIds] = useState([]);
  // The chosen project's template: how its new plans are priced by default.
  const [projectTpl, setProjectTpl] = useState(null);

  useEffect(() => {
    (async () => {
      const [md, cs, ps, rCms, rHims, rCmsOn, rHimsOn] = await Promise.all([
        fetch(QUOTATION_MODULES_URL),
        fetch(`${CUSTOMERSLIST}?page_no=1&page_size=500&customer_category=prospect`),
        fetch(PLAN_SERVICES_URL),
        fetch(`${PRICING_RATES_URL}?product=cms&deployment=cloud`),
        fetch(`${PRICING_RATES_URL}?product=hims&deployment=cloud`),
        // Refused with 403 for a tenant without on-premise, which the API
        // wrapper turns into a silent {status:'fail'} — no toast, no cost.
        fetch(`${PRICING_RATES_URL}?product=cms&deployment=onprem`),
        fetch(`${PRICING_RATES_URL}?product=hims&deployment=onprem`),
      ]);
      if (md?.status === "success") setModules(md.data || []);
      if (ps?.status === "success") setPlanServices(ps.data || []);
      const rows = (r) => (r?.status === "success" ? (r.data || []) : []);
      setPricingRates({
        cloud:  { cms: rows(rCms),   hims: rows(rHims)   },
        onprem: { cms: rows(rCmsOn), hims: rows(rHimsOn) },
      });
      // Two independent signals, because either alone has a silent failure mode.
      // The flag is the intended answer; a 200 on the on-premise sheet is the
      // corroborating one, since the server refuses that read outright (403)
      // for a tenant not entitled to it. Trusting only the flag meant one
      // missing field hid the option with no way to tell why.
      setOnPremAvailable(
        !!rCms?.on_premise_available ||
        rCmsOn?.status === "success" ||
        rHimsOn?.status === "success"
      );
      if (cs?.status === "success") {
        const list = cs.data?.results || cs.data || [];
        setCustomers(Array.isArray(list) ? list : []);
      }

      if (isEdit) {
        const q = await fetch(QUOTATION_DETAIL_URL(id));
        if (q?.status === "success") {
          const d = q.data;
          setQuoteInfo(d);
          setForm({
            customer_id: d.customer_id,
            project_id: d.project_id || null,
            quote_date: d.quote_date,
            valid_until: d.valid_until,
            notes: d.notes || "",
            plans: (d.plans || []).map((p, idx) => ({
              id: p.id,
              plan_label: p.plan_label,
              package_id: p.package_id,
              package_title: p.package_title,
              max_beds: p.max_beds,
              transaction_count_per_year: p.transaction_count_per_year,
              base_price: p.base_price,
              discount_amount: p.discount_amount,
              gst_rate: p.gst_rate,
              amc_rate: p.amc_rate,
              is_recommended: p.is_recommended,
              sort_order: p.sort_order ?? idx,
              items: p.items || [],
              pricing_product:     p.pricing_product     || "",
              pricing_plan_tier:   p.pricing_plan_tier   || "",
              pricing_op_per_year: p.pricing_op_per_year || 0,
              pricing_deployment:  normDeployment(p.pricing_deployment),
              unit_label:     p.unit_label || "",
              unit_count:     p.unit_count || 0,
              unit_price:     p.unit_price || 0,
              billing_months: p.billing_months || 1,
            })),
          });
        }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadLeadProjects = async (customerId) => {
    if (!customerId) { setLeadProjects([]); return; }
    const res = await fetch(QUOTATION_CUSTOMER_PROJECTS_URL, { customer_id: customerId });
    setLeadProjects(res?.status === "success" ? (res.data?.projects || []) : []);
  };
  useEffect(() => { loadLeadProjects(form.customer_id); }, [form.customer_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // This quotation starts selected for merging; the rest are the user's pick.
  useEffect(() => { setMergeIds(id ? [Number(id)] : []); }, [id]);

  // A new quote with no project picked goes to the lead's main project.
  useEffect(() => {
    if (form.project_id || !leadProjects.length) return;
    const main = leadProjects.find(p => p.is_main) || leadProjects[0];
    setForm(prev => ({ ...prev, project_id: main.id }));
  }, [leadProjects]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!form.project_id) { setProjectTpl(null); return; }
    fetch(QUOTATION_TEMPLATE_URL, { project_id: form.project_id }).then(res => {
      const t = res?.status === "success" ? res.data : null;
      setProjectTpl(t);
      // A new quote whose plans nobody has priced yet takes the project's
      // pricing style: per user for TrustEdge, per employee for HRZyra...
      if (!isEdit && t?.default_unit_label) {
        setForm(prev => ({
          ...prev,
          plans: prev.plans.map(p => (Number(p.base_price) || isPerUnit(p)) ? p : {
            ...p, unit_label: t.default_unit_label,
            billing_months: Number(t.default_billing_months) === 1 ? 1 : 12,
          }),
        }));
      }
    });
  }, [form.project_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleMerge = (qid) => setMergeIds(prev =>
    prev.includes(qid) ? prev.filter(x => x !== qid) : [...prev, qid]);

  // Deployment drives the rate sheet, which decides which tiers and OP counts
  // exist at all — so switching it has to pull the tier along rather than leave
  // an Advanced selection that no on-premise sheet can price.
  const setDeployment = (idx, value) => {
    setForm(prev => ({
      ...prev,
      plans: prev.plans.map((p, i) => {
        if (i !== idx) return p;
        const allowed = TIERS_BY_DEPLOYMENT[value] || TIERS_BY_DEPLOYMENT.cloud;
        const sheet   = pricingRates[value]?.[p.pricing_product] || [];
        const opKept  = sheet.some(r => r.op_count === Number(p.pricing_op_per_year));
        return {
          ...p,
          pricing_deployment:  value,
          pricing_plan_tier:   allowed.includes(p.pricing_plan_tier)
                                 ? p.pricing_plan_tier
                                 : (allowed.length === 1 ? allowed[0] : ""),
          // Clearing an OP count the new sheet has no tier for is kinder than
          // leaving a number selected that Calculate would then reject.
          pricing_op_per_year: opKept ? p.pricing_op_per_year : 0,
        };
      }),
    }));
  };

  const tierOptsFor = (plan) => {
    const allowed = TIERS_BY_DEPLOYMENT[normDeployment(plan.pricing_deployment)];
    return TIER_OPTS.filter(o => allowed.includes(o.value));
  };

  const opOptsFor = (plan) => {
    const sheet = pricingRates[normDeployment(plan.pricing_deployment)] || {};
    return (sheet[plan.pricing_product] || sheet.cms || []).map(r => ({
      value: r.op_count,
      label: r.op_count.toLocaleString("en-IN"),
    }));
  };

  // A tenant that does not sell on-premise is not offered the choice at all.
  const deployOpts = onPremAvailable ? DEPLOY_OPTS : DEPLOY_OPTS.filter(o => o.value === "cloud");

  const setPlan = (idx, key, val) => {
    setForm(prev => ({
      ...prev,
      plans: prev.plans.map((p, i) => i === idx ? { ...p, [key]: val } : p),
    }));
  };

  // Compare the plan's current feature modules against the tier's default bundle.
  // Return the effective txn/OP after subtracting removed default modules' txn/visit.
  // Non-default modules added as features do NOT inflate the base price.
  const effectiveTxnForPlan = (plan) => {
    const svc = planServices.find(s =>
      s.product === plan.pricing_product && s.plan === plan.pricing_plan_tier
    );
    if (!svc) return null;
    const tierDefault = (svc.module_ids || []).reduce((sum, id) => {
      const mod = modules.find(m => m.id === id);
      return sum + Number(mod?.txn_per_visit || 0);
    }, 0);
    const defaultIds  = svc.module_ids || [];
    const selectedIds = (plan.items || [])
      .filter(it => it.kind === "feature" && it.module_id)
      .map(it => it.module_id);
    const removedIds  = defaultIds.filter(id => !selectedIds.includes(id));
    const removedTxn  = removedIds.reduce((sum, id) => {
      const mod = modules.find(m => m.id === id);
      return sum + Number(mod?.txn_per_visit || 0);
    }, 0);
    return {
      tierDefault,
      removedCount: removedIds.length,
      removedTxn,
      effective: Math.max(0, tierDefault - removedTxn),
    };
  };

  // Lookup price from rate table and optionally seed default modules.
  const runCalculator = async (idx, { seedDefaults = false } = {}) => {
    const plan = form.plans[idx];
    if (!plan.pricing_product)   return ToastMsg("Pick a product (CMS/HIMS)", "error");
    if (!plan.pricing_plan_tier) return ToastMsg("Pick a plan tier (Essential/Advanced/Enterprise)", "error");
    // Resolve the sheet first: with no tiers on it the OP dropdown is empty,
    // and "enter OP visits per year" would point at a field that cannot be
    // filled. What is missing is the sheet, not the input.
    const deployment  = normDeployment(plan.pricing_deployment);
    const deployLabel = DEPLOY_OPTS.find(o => o.value === deployment)?.label || deployment;
    const productLbl  = PRODUCT_OPTS.find(o => o.value === plan.pricing_product)?.label || plan.pricing_product;
    const rateRows    = pricingRates[deployment]?.[plan.pricing_product] || [];

    // A quote saved before on-premise was Enterprise-only could still carry a
    // tier this sheet does not sell. Say so rather than reporting "no price",
    // which sends people to fix a rate that was never meant to exist.
    if (!TIERS_BY_DEPLOYMENT[deployment].includes(plan.pricing_plan_tier)) {
      return ToastMsg(`${deployLabel} sells ${TIERS_BY_DEPLOYMENT[deployment].join(", ")} only — change the plan tier`, "error");
    }

    if (!rateRows.length) {
      return ToastMsg(`No ${deployLabel} tiers exist for ${productLbl} — add them in Settings → Tier Pricing`, "error");
    }

    const op = Number(plan.pricing_op_per_year || 0);
    if (!op || op <= 0) return ToastMsg("Enter OP visits per year (> 0)", "error");
    const row = rateRows.find(r => r.op_count === op);
    if (!row) return ToastMsg(`No ${deployLabel} pricing for ${op.toLocaleString("en-IN")} OP — check Settings → Tier Pricing`, "error");

    const price = Number(row[plan.pricing_plan_tier] || 0);
    if (!price) return ToastMsg(`No ${deployLabel} price set for ${plan.pricing_plan_tier} at ${op.toLocaleString("en-IN")} OP`, "error");

    const tierLabel = TIER_OPTS.find(o => o.value === plan.pricing_plan_tier)?.label || "";
    const pdfTitle  = productLbl && tierLabel ? `${productLbl} — ${tierLabel}` : plan.package_title;

    // Seed feature items from plan services (Settings → Plan Services)
    let nextItems = plan.items;
    if (seedDefaults) {
      const svc = planServices.find(s => s.product === plan.pricing_product && s.plan === plan.pricing_plan_tier);
      const defaultIds = svc?.module_ids || [];
      const addons = (plan.items || []).filter(it => it.kind === "add_on");
      const features = defaultIds.map((mid, i) => {
        const mod = modules.find(m => m.id === mid);
        return {
          module_id: mid,
          kind: "feature",
          name: mod?.name || "",
          description: mod?.description || "",
          price: 0,
          unit_label: "",
          sort_order: i,
        };
      });
      nextItems = [...features, ...addons.map((a, i) => ({ ...a, sort_order: features.length + i }))];
    }

    setForm(prev => ({
      ...prev,
      plans: prev.plans.map((p, i) => i === idx ? {
        ...p,
        base_price: price,
        package_title: pdfTitle,
        max_beds: plan.pricing_product === "cms" ? 0 : p.max_beds,
        items: nextItems,
      } : p),
    }));
    ToastMsg(`Price: ₹${Number(price).toLocaleString("en-IN")} / yr`, "success");
  };

  const [versions, setVersions] = useState([]);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [activeVersion, setActiveVersion] = useState(null); // full snapshot for read-only view

  const loadVersions = async () => {
    if (!id) return;
    const res = await fetch(QUOTATION_VERSIONS_URL(id));
    if (res?.status === "success") setVersions(res.data || []);
  };

  const viewVersion = async (vno) => {
    const res = await fetch(QUOTATION_VERSION_URL(id, vno));
    if (res?.status === "success") setActiveVersion(res.data);
    else ToastMsg(res?.message || "Failed to load version", "error");
  };

  const freezePlan = async (planIdx) => {
    if (!isEdit || !id) return ToastMsg("Save the quotation first", "error");
    const plan = form.plans[planIdx];
    if (!plan?.id) return ToastMsg("Save the quotation first (this plan hasn't been saved yet)", "error");
    if (quoteInfo?.status === "accepted") return ToastMsg("Quotation is already finalized", "info");
    if (quoteInfo?.approval_status === "pending")
      return ToastMsg("Discount approval is still pending — get it approved first", "error");

    const confirmMsg = `Freeze this quotation with "${plan.plan_label}"?\n\nThis marks the quote accepted and locks further edits. Onboard the client manually from the Onboarded section when ready.`;
    if (!window.confirm(confirmMsg)) return;
    const remarks = window.prompt("Remarks (e.g. 'Client confirmed on call'):", "") || "";

    const res = await create(QUOTATION_FINALIZE_URL(id), { plan_id: plan.id, remarks });
    if (res?.status === "success") {
      ToastMsg("Quotation finalized", "success");
      const q = await fetch(QUOTATION_DETAIL_URL(id));
      if (q?.status === "success") setQuoteInfo(q.data);
    } else {
      ToastMsg(res?.message || "Failed to finalize", "error");
    }
  };

  const doApprovalAction = async (action) => {
    if (!isEdit || !id) return;
    const label = action === "approve" ? "approve" : "reject";
    const remarks = window.prompt(`${label[0].toUpperCase() + label.slice(1)} remarks (optional):`, "");
    if (remarks === null) return; // user cancelled
    const url = action === "approve" ? QUOTATION_APPROVE_URL(id) : QUOTATION_REJECT_URL(id);
    const res = await create(url, { remarks });
    if (res?.status === "success") {
      ToastMsg(`Quotation ${action}d`, "success");
      const q = await fetch(QUOTATION_DETAIL_URL(id));
      if (q?.status === "success") setQuoteInfo(q.data);
    } else {
      ToastMsg(res?.message || `Failed to ${action}`, "error");
    }
  };

  const calcFromFormula = (idx) => {
    const plan = form.plans[idx];
    const hasFeatures = (plan.items || []).some(it => it.kind === "feature");
    // Ask before wiping user-added features
    if (hasFeatures && !window.confirm(
      "Replace the current feature list with the tier's default services?\n\nOK = replace (keeps add-ons)\nCancel = keep current items, just recalculate price"
    )) {
      return runCalculator(idx);
    }
    return runCalculator(idx, { seedDefaults: true });
  };

  const recalcFromServices = (idx) => {
    const eff = effectiveTxnForPlan(form.plans[idx]);
    if (!eff) return ToastMsg("Pick Product + Plan tier first, then Calculate once to set up defaults", "error");
    if (eff.removedCount === 0) {
      return ToastMsg("No default services removed — price hasn't changed from tier baseline", "info");
    }
    runCalculator(idx);
  };

  const addPlan = () => {
    if (form.plans.length >= 3) return ToastMsg("Max 3 plans per quotation", "error");
    const next = blankPlan(form.plans.length);
    if (projectTpl?.default_unit_label) {
      next.unit_label = projectTpl.default_unit_label;
      next.billing_months = Number(projectTpl.default_billing_months) === 1 ? 1 : 12;
    }
    setForm(prev => ({ ...prev, plans: [...prev.plans, next] }));
  };
  const delPlan = (idx) => {
    if (form.plans.length === 1) return ToastMsg("At least one plan required", "error");
    setForm(prev => ({ ...prev, plans: prev.plans.filter((_, i) => i !== idx) }));
  };

  const addItem = (pIdx, kind) => {
    setPlan(pIdx, "items", [
      ...form.plans[pIdx].items,
      { module_id: null, kind, name: "", description: "", price: 0, unit_label: "", quantity: 1, sort_order: form.plans[pIdx].items.length },
    ]);
  };
  const updItem = (pIdx, iIdx, key, val) => {
    const items = form.plans[pIdx].items.map((it, j) => j === iIdx ? { ...it, [key]: val } : it);
    setPlan(pIdx, "items", items);
  };
  const delItem = (pIdx, iIdx) => {
    setPlan(pIdx, "items", form.plans[pIdx].items.filter((_, j) => j !== iIdx));
  };
  const pickModuleForItem = (pIdx, iIdx, moduleId) => {
    const mod = modules.find(m => m.id === moduleId);
    if (!mod) return;
    const items = form.plans[pIdx].items.map((it, j) => j === iIdx ? {
      ...it, module_id: moduleId, name: mod.name, description: mod.description || "",
      price: mod.price, unit_label: mod.unit_label || "",
    } : it);
    setPlan(pIdx, "items", items);
  };

  const planTotals = (plan) => {
    const custom = customTotal(plan);
    const base   = subTotal(plan);
    const disc   = Number(plan.discount_amount || 0);
    const gstR   = Number(plan.gst_rate || 0);
    const net    = Math.max(0, base - disc);
    const gst    = (net * gstR) / 100;
    return { custom, sub: base, net, gst, total: net + gst };
  };

  const save = async () => {
    if (!form.customer_id) return ToastMsg("Select a customer", "error");
    if (!form.plans.length) return ToastMsg("Add at least one plan", "error");
    for (const p of form.plans) {
      if (!p.plan_label?.trim()) return ToastMsg("Every plan needs a label", "error");
    }
    for (const p of form.plans) {
      if (isPerUnit(p) && !(unitBase(p) > 0)) {
        return ToastMsg("error", `${p.plan_label}: enter the number of ${p.unit_label.toLowerCase()}s and the price per ${p.unit_label.toLowerCase()}`);
      }
      if ((p.items || []).some(it => it.kind === "line" && !(it.name || "").trim())) {
        return ToastMsg("error", `${p.plan_label}: every custom line needs a description`);
      }
    }
    setSaving(true);
    // The server recomputes a per-unit base price itself; sending it keeps the
    // discount-approval check and the saved figure in agreement.
    const payload = { ...form, plans: form.plans.map(p => (isPerUnit(p) && unitBase(p) > 0)
      ? { ...p, base_price: unitBase(p) } : p) };
    const res = isEdit
      ? await update(QUOTATION_DETAIL_URL(id), payload)
      : await create(QUOTATIONS_URL, payload);
    setSaving(false);
    if (res?.status === "success") {
      ToastMsg(`Quotation ${isEdit ? "updated" : "created"}`, "success");
      nav(`/quotations/${res.data?.id || res.id || id}/print`);
    } else {
      ToastMsg(res?.message || "Failed", "error");
    }
  };

  return (
    <ContainerCard>
      <div className="px-6 py-4 space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => nav("/quotations")} className="p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"><ArrowLeft className="h-4 w-4" /></button>
            <div>
              <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                {isEdit ? `Edit Quotation ${quoteInfo?.quote_number || ""}` : "New Quotation"}
              </h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">Up to 3 plans can be presented to the customer.</p>
            </div>
          </div>
          <div className="flex gap-2">
            {isEdit && quoteInfo?.approval_status === "pending" && canEdit && (
              <>
                <Button size="sm" variant="outline" className="text-green-700 border-green-400" onClick={() => doApprovalAction("approve")}>
                  Approve
                </Button>
                <Button size="sm" variant="outline" className="text-red-600 border-red-400" onClick={() => doApprovalAction("reject")}>
                  Reject
                </Button>
              </>
            )}
            {isEdit && (
              <Button size="sm" variant="outline" onClick={() => { loadVersions(); setVersionsOpen(v => !v); }}>
                <History className="h-3.5 w-3.5 mr-1" /> Versions
              </Button>
            )}
            {isEdit && (
              <Button size="sm" variant="outline"
                onClick={() => window.open(`/quotations/${id}/kpi`, "_blank")}>
                <TrendingUp className="h-3.5 w-3.5 mr-1" /> KPI
              </Button>
            )}
            {isEdit && <Button size="sm" variant="outline" onClick={() => nav(`/quotations/${id}/print`)}><Printer className="h-3.5 w-3.5 mr-1" /> Print</Button>}
            {canSave && (
              <Button size="sm" onClick={save} disabled={saving}><Save className="h-3.5 w-3.5 mr-1" /> {saving ? "Saving..." : "Save"}</Button>
            )}
          </div>
        </div>

        {/* Versions panel */}
        {isEdit && versionsOpen && (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Version history</h3>
              <button onClick={() => setVersionsOpen(false)} className="text-gray-400 p-1 hover:text-gray-700"><X className="h-4 w-4" /></button>
            </div>
            {versions.length === 0 ? (
              <p className="text-xs text-gray-500">No prior versions — every edit from now on will be logged.</p>
            ) : (
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-100 dark:border-gray-700">
                    <th className="py-1.5">v#</th>
                    <th className="py-1.5">When</th>
                    <th className="py-1.5">By</th>
                    <th className="py-1.5">Note</th>
                    <th className="py-1.5 w-16"></th>
                  </tr>
                </thead>
                <tbody>
                  {versions.map(v => (
                    <tr key={v.version_no} className="border-b border-gray-50 dark:border-gray-700/50">
                      <td className="py-1.5 font-mono text-gray-700 dark:text-gray-300">v{v.version_no}</td>
                      <td className="py-1.5 text-gray-500">{v.created_date ? dayjs(v.created_date).format("DD MMM YYYY HH:mm") : "—"}</td>
                      <td className="py-1.5 text-gray-500">{v.snapshot_by || "—"}</td>
                      <td className="py-1.5 text-gray-500">{v.change_note || "—"}</td>
                      <td className="py-1.5">
                        <button onClick={() => viewVersion(v.version_no)} className="text-primary hover:underline">View</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Read-only version viewer modal */}
        {activeVersion && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setActiveVersion(null)}>
            <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-[90%] max-h-[85vh] overflow-auto p-5" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                  Version {activeVersion.version_no} — {activeVersion.snapshot_by || "unknown"}
                  <span className="ml-2 text-xs text-gray-500">{activeVersion.created_date ? dayjs(activeVersion.created_date).format("DD MMM YYYY HH:mm") : ""}</span>
                </h3>
                <button onClick={() => setActiveVersion(null)} className="text-gray-400 p-1 hover:text-gray-700"><X className="h-4 w-4" /></button>
              </div>
              {activeVersion.change_note && <p className="text-xs text-gray-500 mb-2">Note: {activeVersion.change_note}</p>}
              <pre className="text-[11px] bg-gray-50 dark:bg-gray-900/60 rounded p-3 overflow-auto text-gray-800 dark:text-gray-200">
                {JSON.stringify(activeVersion.snapshot, null, 2)}
              </pre>
            </div>
          </div>
        )}

        {/* Approval status banner */}
        {isEdit && quoteInfo?.approval_status && quoteInfo.approval_status !== "not_required" && (
          <div className={`rounded-md p-3 text-sm border ${
            quoteInfo.approval_status === "pending"  ? "bg-yellow-50 border-yellow-300 text-yellow-900 dark:bg-yellow-900/20 dark:text-yellow-100" :
            quoteInfo.approval_status === "approved" ? "bg-green-50 border-green-300 text-green-900 dark:bg-green-900/20 dark:text-green-100" :
                                                       "bg-red-50 border-red-300 text-red-900 dark:bg-red-900/20 dark:text-red-100"
          }`}>
            <b className="uppercase text-[11px]">{quoteInfo.approval_status.replace("_", " ")}</b>
            {" — requested discount "}<b>{quoteInfo.approval_requested_discount_pct}%</b>
            {" · role cap "}<b>{quoteInfo.approval_required_cap}%</b>
            {quoteInfo.approved_by_name && <> · by <b>{quoteInfo.approved_by_name}</b></>}
            {quoteInfo.approval_remarks && <> · "{quoteInfo.approval_remarks}"</>}
          </div>
        )}

        {/* Header */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div className="md:col-span-2">
              <Label>Customer *</Label>
              <Select className="w-full" placeholder="Select a prospect"
                showSearch optionFilterProp="label"
                value={form.customer_id}
                onChange={v => setForm(p => ({ ...p, customer_id: v, project_id: null }))}
                options={customers.map(c => ({ value: c.id, label: c.customer_name }))} />
            </div>
            {leadProjects.length > 0 && (
              <div className="md:col-span-2">
                <Label>Project (product quoted)</Label>
                <Select className="w-full" value={form.project_id || undefined}
                  onChange={v => setForm(p => ({ ...p, project_id: v }))}
                  options={leadProjects.map(p => ({
                    value: p.id,
                    label: `${p.name}${p.is_main ? " (main)" : ""}${p.has_template ? "" : " · default template"}`,
                  }))} />
              </div>
            )}
            <div>
              <Label>Quote Date</Label>
              <Input type="date" value={form.quote_date} onChange={e => setForm(p => ({ ...p, quote_date: e.target.value }))} />
            </div>
            <div>
              <Label>Valid Until</Label>
              <Input type="date" value={form.valid_until} onChange={e => setForm(p => ({ ...p, valid_until: e.target.value }))} />
            </div>
            <div className="md:col-span-4">
              <Label>Notes (internal)</Label>
              <Textarea rows={2} value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} />
            </div>
          </div>
        </div>

        {/* The lead's quotations in each project it is interested in */}
        {form.customer_id && leadProjects.length > 1 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-3">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-1.5">
                  <Layers className="h-4 w-4 text-gray-400" /> Quotations across this lead's projects
                </h3>
                <p className="text-[11px] text-gray-500">Tick two or more to print them as one merged proposal.</p>
              </div>
              <Button size="sm" disabled={mergeIds.length < 2}
                onClick={() => nav(`/quotations/merged?ids=${mergeIds.join(",")}`)}>
                <Printer className="h-3.5 w-3.5 mr-1" /> Print merged ({mergeIds.length})
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {leadProjects.map(p => (
                <div key={p.id} className={`rounded-md border p-3 space-y-2 ${p.id === form.project_id ? "border-blue-300 bg-blue-50/40 dark:bg-blue-900/10" : "border-gray-200 dark:border-gray-700"}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-gray-800 dark:text-gray-200">
                      {p.name}{p.is_main && <span className="ml-1 text-[10px] font-normal text-gray-400">main</span>}
                    </span>
                    <button className="text-[11px] text-blue-600 hover:underline flex items-center gap-0.5"
                      onClick={() => nav(`/quotations/new?customer_id=${form.customer_id}&project_id=${p.id}`)}>
                      <FilePlus className="h-3 w-3" /> New
                    </button>
                  </div>
                  {p.quotations.length === 0 ? (
                    <p className="text-[11px] text-gray-400">No quotation yet for {p.name}.</p>
                  ) : p.quotations.map(q => (
                    <label key={q.id} className="flex items-center gap-2 text-[11px] text-gray-600 dark:text-gray-300">
                      <input type="checkbox" checked={mergeIds.includes(q.id)} onChange={() => toggleMerge(q.id)} />
                      <button type="button" className={`font-mono hover:underline ${q.id === Number(id) ? "font-bold text-gray-900 dark:text-white" : "text-blue-600"}`}
                        onClick={() => q.id !== Number(id) && nav(`/quotations/${q.id}`)}>
                        {q.quote_number}
                      </button>
                      <span className="capitalize text-gray-400">{q.status}</span>
                      <span className="ml-auto">{fmt(q.grand_total)}</span>
                    </label>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Plans */}
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white">Plans presented to client ({form.plans.length}/3)</h2>
          {form.plans.length < 3 && <Button size="sm" variant="outline" onClick={addPlan}><Plus className="h-3.5 w-3.5 mr-1" /> Add Plan</Button>}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
          {form.plans.map((plan, idx) => {
            const totals = planTotals(plan);
            return (
              <div key={idx} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <Input className="w-40" value={plan.plan_label} onChange={e => setPlan(idx, "plan_label", e.target.value)} />
                  <div className="flex items-center gap-2">
                    <label className="text-[11px] flex items-center gap-1 text-gray-500">
                      <input type="checkbox" checked={plan.is_recommended} onChange={e => setPlan(idx, "is_recommended", e.target.checked)} />
                      Recommended
                    </label>
                    {form.plans.length > 1 && <button onClick={() => delPlan(idx)} className="text-red-500 p-1"><X className="h-4 w-4" /></button>}
                  </div>
                </div>

                {/* How this plan is priced */}
                <div className="flex rounded-md border border-gray-200 dark:border-gray-700 overflow-hidden text-[11px]">
                  {[["unit", "Per unit (users / employees / beds)"], ["tier", "Tier calculator (CMS / HIMS)"]].map(([k, lbl]) => {
                    const on = (k === "unit") === isPerUnit(plan);
                    return (
                      <button key={k} type="button"
                        className={`flex-1 px-2 py-1 ${on ? "bg-gray-900 text-white dark:bg-white dark:text-gray-900" : "text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-700"}`}
                        onClick={() => setPlan(idx, "unit_label", k === "unit" ? (projectTpl?.default_unit_label || plan.unit_label || "User") : "")}>
                        {lbl}
                      </button>
                    );
                  })}
                </div>

                {isPerUnit(plan) && (
                  <div className="bg-emerald-50/60 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-md p-3 space-y-2">
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label>Unit</Label>
                        <Select className="w-full" size="small" value={plan.unit_label}
                          onChange={v => setPlan(idx, "unit_label", v)}
                          options={[...new Set(["User", "Employee", "Bed", "Device", plan.unit_label].filter(Boolean))]
                            .map(u => ({ value: u, label: u }))} />
                      </div>
                      <div>
                        <Label>Billing</Label>
                        <Select className="w-full" size="small" value={Number(plan.billing_months) === 12 ? 12 : 1}
                          onChange={v => setPlan(idx, "billing_months", v)}
                          options={[{ value: 12, label: "Monthly rate × 12 (per year)" },
                                    { value: 1, label: "One-time / annual price" }]} />
                      </div>
                      <div>
                        <Label>No. of {plan.unit_label.toLowerCase()}s</Label>
                        <Input type="number" min={0} value={plan.unit_count} onChange={e => setPlan(idx, "unit_count", e.target.value)} />
                      </div>
                      <div>
                        <Label>Cost / {plan.unit_label.toLowerCase()} (₹)</Label>
                        <Input type="number" min={0} value={plan.unit_price} onChange={e => setPlan(idx, "unit_price", e.target.value)} />
                      </div>
                    </div>
                    <p className="text-[11px] text-emerald-800 dark:text-emerald-200">
                      {Number(plan.unit_count || 0).toLocaleString("en-IN")} × {fmt(plan.unit_price)}
                      {Number(plan.billing_months) === 12 ? " × 12 months" : ""} = <b>{fmt(unitBase(plan))}</b> base price
                    </p>
                  </div>
                )}

                {/* Calculate from Product Pricing formula */}
                {!isPerUnit(plan) && (
                <div className="bg-indigo-50/60 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-md p-3 space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    {/* Deployment leads: it decides which sheet is read, and so
                        which tiers and OP counts the pickers below can offer. */}
                    <div>
                      <Label>Deployment</Label>
                      <Select className="w-full" size="small"
                        value={normDeployment(plan.pricing_deployment)}
                        onChange={(v) => setDeployment(idx, v)}
                        options={deployOpts} />
                    </div>
                    <div>
                      <Label>Product</Label>
                      <Select className="w-full" size="small" placeholder="CMS / HIMS"
                        value={plan.pricing_product || undefined}
                        onChange={(v) => setPlan(idx, "pricing_product", v)}
                        options={PRODUCT_OPTS} />
                    </div>
                    <div>
                      <Label>Plan tier</Label>
                      <Select className="w-full" size="small" placeholder="Tier"
                        value={plan.pricing_plan_tier || undefined}
                        onChange={(v) => setPlan(idx, "pricing_plan_tier", v)}
                        options={tierOptsFor(plan)} />
                    </div>
                    <div>
                      <Label>OP visits / yr</Label>
                      <Select className="w-full" size="small"
                        placeholder={opOptsFor(plan).length ? "Select OP count" : "No tiers configured"}
                        value={plan.pricing_op_per_year || undefined}
                        onChange={(v) => setPlan(idx, "pricing_op_per_year", v)}
                        showSearch
                        options={opOptsFor(plan)}
                        notFoundContent="No tiers on this sheet — add them in Settings → Tier Pricing"
                      />
                    </div>
                  </div>
                  <Button size="sm" className="w-full" onClick={() => calcFromFormula(idx)}>
                    <Calculator className="h-3 w-3 mr-1" /> Calculate &amp; fill base price
                  </Button>
                </div>
                )}

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label>PDF Title</Label>
                    <Input value={plan.package_title} onChange={e => setPlan(idx, "package_title", e.target.value)} />
                  </div>
                  {plan.pricing_product !== "cms" && !isPerUnit(plan) && (
                    <div>
                      <Label>Max Beds</Label>
                      <Input type="number" value={plan.max_beds} onChange={e => setPlan(idx, "max_beds", e.target.value)} />
                    </div>
                  )}
                  <div>
                  </div>
                  <div>
                    <Label>Base Price (₹){isPerUnit(plan) ? " — from units" : ""}</Label>
                    <Input type="number" disabled={isPerUnit(plan)}
                      value={isPerUnit(plan) ? unitBase(plan) : plan.base_price}
                      onChange={e => setPlan(idx, "base_price", e.target.value)} />
                  </div>
                  <div>
                    <Label>Discount</Label>
                    <div className="grid grid-cols-2 gap-1">
                      <Input
                        type="number"
                        placeholder="₹"
                        value={plan.discount_amount}
                        onChange={e => setPlan(idx, "discount_amount", e.target.value)}
                      />
                      <Input
                        type="number"
                        placeholder="%"
                        value={(() => {
                          const base = subTotal(plan);
                          const disc = Number(plan.discount_amount || 0);
                          if (!base) return "";
                          const pct = (disc / base) * 100;
                          return pct ? Number(pct.toFixed(2)) : "";
                        })()}
                        onChange={e => {
                          const base = subTotal(plan);
                          const pct  = Number(e.target.value || 0);
                          setPlan(idx, "discount_amount", base > 0 ? +(base * pct / 100).toFixed(2) : 0);
                        }}
                      />
                    </div>
                  </div>
                  <div>
                    <Label>GST %</Label>
                    <Input type="number" value={plan.gst_rate} onChange={e => setPlan(idx, "gst_rate", e.target.value)} />
                  </div>
                  <div>
                    <Label>AMC %</Label>
                    <Input type="number" value={plan.amc_rate} onChange={e => setPlan(idx, "amc_rate", e.target.value)} />
                  </div>
                </div>

                {/* Items */}
                <div className="border-t border-gray-200 dark:border-gray-700 pt-3 space-y-2">
                  <div className="flex items-center justify-between">
                    <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300">Features / Add-ons</h4>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" onClick={() => addItem(idx, "feature")}><Plus className="h-3 w-3 mr-0.5" /> Feature</Button>
                      <Button size="sm" variant="outline" onClick={() => addItem(idx, "add_on")}><Plus className="h-3 w-3 mr-0.5" /> Add-on</Button>
                      <Button size="sm" variant="outline" onClick={() => addItem(idx, "line")}><Plus className="h-3 w-3 mr-0.5" /> Custom</Button>
                    </div>
                  </div>
                  {plan.items.length === 0 && <p className="text-[11px] text-gray-400">No items yet</p>}
                  {plan.items.map((it, iIdx) => {
                    // A custom line: qty x rate, added to the plan total.
                    if (it.kind === "line") return (
                      <div key={iIdx} className="flex items-start gap-2 bg-amber-50/70 dark:bg-amber-900/20 rounded p-2">
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium mt-1 bg-amber-100 text-amber-800">CUSTOM</span>
                        <div className="flex-1 space-y-1">
                          <Input placeholder="Description (e.g. Customization, Data migration)" value={it.name}
                            onChange={e => updItem(idx, iIdx, "name", e.target.value)} />
                          <div className="grid grid-cols-3 gap-1 items-center">
                            <Input type="number" min={0} placeholder="Qty" value={it.quantity}
                              onChange={e => updItem(idx, iIdx, "quantity", e.target.value)} />
                            <Input type="number" min={0} placeholder="Rate ₹" value={it.price}
                              onChange={e => updItem(idx, iIdx, "price", e.target.value)} />
                            <span className="text-right text-xs font-medium text-gray-800 dark:text-gray-200">{fmt(lineAmount(it))}</span>
                          </div>
                          <Input placeholder="Note (optional)" value={it.description || ""}
                            onChange={e => updItem(idx, iIdx, "description", e.target.value)} />
                          <p className="text-[10px] text-amber-800 dark:text-amber-200">Added to the total</p>
                        </div>
                        <button onClick={() => delItem(idx, iIdx)} className="text-red-500 p-1 mt-1"><X className="h-3.5 w-3.5" /></button>
                      </div>
                    );
                    const linkedMod = it.module_id ? modules.find(m => m.id === it.module_id) : null;
                    const txnBadge  = it.kind === "feature" && linkedMod && linkedMod.txn_per_visit > 0
                      ? ` · ${linkedMod.txn_per_visit} txn/visit` : "";
                    return (
                    <div key={iIdx} className="flex items-start gap-2 bg-gray-50 dark:bg-gray-700/30 rounded p-2">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium mt-1 ${it.kind === "feature" ? "bg-blue-100 text-blue-700" : "bg-purple-100 text-purple-700"}`}>
                        {it.kind === "feature" ? "FEAT" : "ADD"}
                      </span>
                      <div className="flex-1 space-y-1">
                        <Select className="w-full" size="small" placeholder="Pick from catalog (optional)"
                          allowClear showSearch optionFilterProp="label"
                          value={it.module_id || undefined}
                          onChange={(v) => v ? pickModuleForItem(idx, iIdx, v) : updItem(idx, iIdx, "module_id", null)}
                          options={modules.map(m => ({ value: m.id, label: m.name }))} />
                        <Input placeholder="Name" value={it.name} onChange={e => updItem(idx, iIdx, "name", e.target.value)} />
                        {txnBadge && <p className="text-[10px] text-indigo-600 dark:text-indigo-300">{txnBadge.slice(3)}</p>}
                        {it.kind === "add_on" && (
                          <div className="grid grid-cols-2 gap-1">
                            <Input placeholder="Price" type="number" value={it.price} onChange={e => updItem(idx, iIdx, "price", e.target.value)} />
                            <Input placeholder="Unit (e.g. per hr, /Minute)" value={it.unit_label} onChange={e => updItem(idx, iIdx, "unit_label", e.target.value)} />
                            <Input className="col-span-2" placeholder="Note (e.g. Onetime, per device)" value={it.description || ""} onChange={e => updItem(idx, iIdx, "description", e.target.value)} />
                          </div>
                        )}
                      </div>
                      <button onClick={() => delItem(idx, iIdx)} className="text-red-500 p-1 mt-1"><X className="h-3.5 w-3.5" /></button>
                    </div>
                    );
                  })}

                  {/* Effective txn/OP after removing default services — drives price reduction */}
                  {(() => {
                    const eff = effectiveTxnForPlan(plan);
                    if (!eff || eff.tierDefault === 0) return null;
                    const isReduced = eff.removedCount > 0;
                    return (
                      <div className={`mt-2 flex items-center justify-between rounded p-2 border text-[11px] ${
                        isReduced
                          ? "bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800 text-amber-900 dark:text-amber-200"
                          : "bg-gray-50 dark:bg-gray-700/30 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300"
                      }`}>
                        <div>
                          Tier default: <b>{eff.tierDefault} txn/visit</b>
                          {isReduced && <> · Removed {eff.removedCount} service(s), −{eff.removedTxn} · <b>Effective: {eff.effective} txn/visit</b></>}
                        </div>
                        {isReduced && (
                          <Button size="sm" variant="outline" onClick={() => recalcFromServices(idx)}>
                            <Calculator className="h-3 w-3 mr-1" /> Recalc price
                          </Button>
                        )}
                      </div>
                    );
                  })()}
                </div>

                {/* Totals */}
                <div className="border-t border-gray-200 dark:border-gray-700 pt-2 text-xs space-y-1">
                  {totals.custom > 0 && <Row label="Custom lines" value={fmt(totals.custom)} />}
                  {totals.custom > 0 && <Row label="Sub-total" value={fmt(totals.sub)} />}
                  <Row label="Net" value={fmt(totals.net)} />
                  <Row label={`GST (${plan.gst_rate}%)`} value={fmt(totals.gst)} />
                  <Row label="Grand Total" value={fmt(totals.total)} bold />
                </div>

                {/* Freeze this plan — rep says client picked this one */}
                {isEdit && canEdit && (() => {
                  const isFrozenHere = quoteInfo?.status === "accepted" && quoteInfo?.accepted_plan_id === plan.id;
                  const isFrozenElsewhere = quoteInfo?.status === "accepted" && !isFrozenHere;
                  if (isFrozenHere) {
                    return (
                      <div className="mt-2 flex items-center gap-2 bg-green-50 dark:bg-green-900/20 border border-green-300 dark:border-green-700 text-green-900 dark:text-green-200 rounded p-2 text-[11px] font-medium">
                        <Lock className="h-3.5 w-3.5" /> Client finalized this plan
                      </div>
                    );
                  }
                  if (isFrozenElsewhere) return null;
                  return (
                    <Button size="sm" variant="outline" className="w-full mt-1" onClick={() => freezePlan(idx)}>
                      <Lock className="h-3 w-3 mr-1" /> Freeze (client picked this)
                    </Button>
                  );
                })()}
              </div>
            );
          })}
        </div>
      </div>
    </ContainerCard>
  );
}

const Label = ({ children }) => <label className="block text-[11px] font-medium text-gray-600 dark:text-gray-300 mb-0.5">{children}</label>;

const Row = ({ label, value, bold }) => (
  <div className="flex justify-between">
    <span className="text-gray-500">{label}</span>
    <span className={bold ? "font-bold text-gray-900 dark:text-white" : "text-gray-700 dark:text-gray-200"}>{value}</span>
  </div>
);
